import React, { useState } from "react";
import { Button, Card, Container, Form } from "react-bootstrap";
import { db, storage } from "../firebase-config";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import Swal from "sweetalert2";

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];

function ConfigSort() {
  const [selectedImage, setSelectedImage] = useState(null);
  const [title, setTitle] = useState("");
  const [price, setPrice] = useState("");
  const [cifras, setCifras] = useState("2");
  const [minCompras, setMinCompras] = useState("2");
  const [numerosBloqueados, setNumerosBloqueados] = useState([""]);
  const [numerosPremiados, setNumerosPremiados] = useState([""]);
  const [porcentajeLiberacion, setPorcentajeLiberacion] = useState("50");
  const [isSaving, setIsSaving] = useState(false);

  const handleImageChange = (event) => {
    const file = event.target.files[0];

    if (!file) {
      setSelectedImage(null);
      return;
    }

    if (
      !ALLOWED_IMAGE_TYPES.includes(file.type) ||
      file.size > MAX_IMAGE_SIZE
    ) {
      setSelectedImage(null);
      Swal.fire(
        "Error",
        "La imagen debe ser JPG, PNG o WEBP y pesar maximo 5 MB.",
        "error"
      );
      return;
    }

    setSelectedImage(file);
  };

  const handleNumeroBloqueadoChange = (index, value) => {
    setNumerosBloqueados((current) =>
      current.map((numero, currentIndex) =>
        currentIndex === index ? digitsOnly(value, Number(cifras)) : numero
      )
    );
  };

  const handleNumeroPremiadoChange = (index, value) => {
    setNumerosPremiados((current) =>
      current.map((numero, currentIndex) =>
        currentIndex === index ? digitsOnly(value, Number(cifras)) : numero
      )
    );
  };

  const agregarNuevoNumero = () => {
    setNumerosBloqueados((current) => [...current, ""]);
  };

  const eliminarNumeroBloqueado = (index) => {
    setNumerosBloqueados((current) =>
      current.filter((_, currentIndex) => currentIndex !== index)
    );
  };

  const agregarNumeroPremiado = () => {
    setNumerosPremiados((current) => [...current, ""]);
  };

  const eliminarNumeroPremiado = (index) => {
    setNumerosPremiados((current) =>
      current.filter((_, currentIndex) => currentIndex !== index)
    );
  };

  async function handleUpload() {
    if (isSaving) return;

    const payload = buildPayload();
    if (payload.error) {
      Swal.fire("Error", payload.error, "error");
      return;
    }

    const result = await Swal.fire({
      title: "Estas seguro?",
      text: "Se creara un nuevo sorteo con estos datos.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#000000",
      cancelButtonColor: "#d33",
      confirmButtonText: "Si, crear sorteo",
    });

    if (!result.isConfirmed) return;

    setIsSaving(true);

    try {
      const sorteosRef = collection(db, "sorteos");
      const imageRef = ref(storage, `portadas/${createId()}`);

      await uploadBytes(imageRef, selectedImage, {
        contentType: selectedImage.type,
      });
      const url = await getDownloadURL(imageRef);

      await addDoc(sorteosRef, {
        ...payload.data,
        url,
        createdAt: serverTimestamp(),
      });

      Swal.fire("Exito", "Sorteo creado exitosamente.", "success");
      resetForm();
    } catch (error) {
      Swal.fire("Error", "Hubo un problema al guardar el sorteo.", "error");
    } finally {
      setIsSaving(false);
    }
  }

  function buildPayload() {
    const cleanTitle = sanitizeText(title, 160);
    const parsedPrice = Number(price);
    const parsedCifras = Number(cifras);
    const parsedMinCompras = Number(minCompras);
    const parsedPorcentaje = Number(porcentajeLiberacion);

    if (!selectedImage || !cleanTitle) {
      return { error: "Selecciona una imagen y agrega un titulo." };
    }

    if (!Number.isInteger(parsedCifras) || parsedCifras < 1 || parsedCifras > 5) {
      return { error: "Las cifras deben estar entre 1 y 5." };
    }

    if (!Number.isFinite(parsedPrice) || parsedPrice <= 0) {
      return { error: "El valor de la boleta no es valido." };
    }

    if (!Number.isInteger(parsedMinCompras) || parsedMinCompras < 1) {
      return { error: "El minimo de boletas debe ser mayor a cero." };
    }

    if (
      !Number.isInteger(parsedPorcentaje) ||
      parsedPorcentaje < 1 ||
      parsedPorcentaje > 100
    ) {
      return { error: "El porcentaje de liberacion debe estar entre 1 y 100." };
    }

    const numerosRestringidos = normalizeNumbers(
      numerosBloqueados,
      parsedCifras
    );
    const numerosGanadores = normalizeNumbers(numerosPremiados, parsedCifras);

    return {
      data: {
        title: cleanTitle,
        cifras: parsedCifras,
        price: parsedPrice,
        totalNumeros: 10 ** parsedCifras,
        activo: true,
        minCompras: parsedMinCompras,
        porcentajeLiberacion: parsedPorcentaje,
        numerosRestringidos,
        numerosPremiados: numerosGanadores,
      },
    };
  }

  function resetForm() {
    setSelectedImage(null);
    setTitle("");
    setPrice("");
    setCifras("2");
    setMinCompras("2");
    setNumerosBloqueados([""]);
    setNumerosPremiados([""]);
    setPorcentajeLiberacion("50");
  }

  return (
    <div>
      <Container className="mb-5 mt-3">
        <div className="text-start m-3">
          <h2 className="mt-5">Crear nuevo sorteo</h2>
        </div>

        <Card>
          <Card.Body>
            <Form.Group controlId="formFile" className="mb-3">
              <div className="text-start mt-3 mb-3">
                <Form.Label>Portada</Form.Label>
              </div>
              <Form.Control
                type="file"
                accept={ALLOWED_IMAGE_TYPES.join(",")}
                onChange={handleImageChange}
              />

              <div className="text-start mt-3 mb-3">
                <Form.Label>Titulo</Form.Label>
              </div>
              <Form.Control
                type="text"
                id="titulo-dinamica"
                placeholder="Digita el titulo"
                value={title}
                maxLength={160}
                onChange={(event) => setTitle(event.target.value)}
              />

              <div className="text-start mt-3 mb-3">
                <Form.Label>Valor</Form.Label>
              </div>
              <Form.Control
                type="number"
                id="valor-dinamica"
                min="1"
                placeholder="Digita el valor de la boleta"
                value={price}
                onChange={(event) => setPrice(event.target.value)}
              />

              <div className="text-start mt-3 mb-3">
                <Form.Label>Cifras</Form.Label>
              </div>
              <Form.Select
                value={cifras}
                onChange={(event) => {
                  const nextCifras = event.target.value;
                  setCifras(nextCifras);
                  setNumerosBloqueados((current) =>
                    current.map((numero) => digitsOnly(numero, Number(nextCifras)))
                  );
                  setNumerosPremiados((current) =>
                    current.map((numero) => digitsOnly(numero, Number(nextCifras)))
                  );
                }}
              >
                <option value="1">1</option>
                <option value="2">2</option>
                <option value="3">3</option>
                <option value="4">4</option>
                <option value="5">5</option>
              </Form.Select>

              <div className="text-start mt-3 mb-3">
                <Form.Label>Minimo de boletas</Form.Label>
              </div>
              <Form.Control
                type="number"
                id="minimo-boletas"
                min="1"
                placeholder="Digita el minimo de compras"
                value={minCompras}
                onChange={(event) => setMinCompras(event.target.value)}
              />

              <div className="text-start mt-3 mb-3">
                <Form.Label>Porcentaje de liberacion (%)</Form.Label>
              </div>
              <Form.Control
                type="number"
                min="1"
                max="100"
                value={porcentajeLiberacion}
                onChange={(event) => setPorcentajeLiberacion(event.target.value)}
                placeholder="Ej: 50"
              />

              <div className="text-start mt-4 mb-2">
                <Form.Label>Numeros bloqueados</Form.Label>
                {numerosBloqueados.map((numero, index) => (
                  <div key={index} className="d-flex align-items-center mb-2">
                    <Form.Control
                      type="text"
                      inputMode="numeric"
                      placeholder={`Numero bloqueado #${index + 1}`}
                      value={numero}
                      maxLength={Number(cifras)}
                      onChange={(event) =>
                        handleNumeroBloqueadoChange(index, event.target.value)
                      }
                      className="me-2"
                    />
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => eliminarNumeroBloqueado(index)}
                    >
                      Eliminar
                    </Button>
                  </div>
                ))}
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={agregarNuevoNumero}
                >
                  Agregar nuevo numero
                </Button>
              </div>

              <div className="text-start mt-4 mb-2">
                <Form.Label>Numeros premiados</Form.Label>
                {numerosPremiados.map((numero, index) => (
                  <div key={index} className="d-flex align-items-center mb-2">
                    <Form.Control
                      type="text"
                      inputMode="numeric"
                      placeholder={`Numero premiado #${index + 1}`}
                      value={numero}
                      maxLength={Number(cifras)}
                      onChange={(event) =>
                        handleNumeroPremiadoChange(index, event.target.value)
                      }
                      className="me-2"
                    />
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => eliminarNumeroPremiado(index)}
                    >
                      Eliminar
                    </Button>
                  </div>
                ))}
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={agregarNumeroPremiado}
                >
                  Anadir numero premiado
                </Button>
              </div>
            </Form.Group>
          </Card.Body>

          <Button variant="dark" onClick={handleUpload} disabled={isSaving}>
            {isSaving ? "Guardando..." : "Guardar cambios"}
          </Button>
        </Card>
      </Container>
    </div>
  );
}

function createId() {
  if (window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function digitsOnly(value, maxLength) {
  return String(value || "")
    .replace(/\D/g, "")
    .slice(0, maxLength);
}

function normalizeNumbers(values, cifras) {
  return Array.from(
    new Set(
      values
        .map((value) => digitsOnly(value, cifras))
        .filter(Boolean)
        .map((value) => value.padStart(cifras, "0"))
    )
  );
}

function sanitizeText(value, maxLength) {
  return String(value || "")
    .replace(/[\r\n]+/g, " ")
    .trim()
    .slice(0, maxLength);
}

export default ConfigSort;
